import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, CornerUpLeft, CornerUpRight, ArrowUpLeft, ArrowUpRight, Undo2, Flag, RotateCw, Navigation } from 'lucide-react';
import { useTrackRec } from '../lib/trackRec';
import { buildNav, navState, stepKey, stepText, fetchRoute } from '../lib/navi';
import { speak } from '../lib/a11y';
import { STOP_ICON } from '../lib/routeStops';

const ICON = { left: CornerUpLeft, sharp_left: CornerUpLeft, right: CornerUpRight, sharp_right: CornerUpRight, slight_left: ArrowUpLeft, slight_right: ArrowUpRight, uturn: Undo2, arrive: Flag, roundabout: RotateCw, straight: ArrowUp };
const bearing = (a, b) => { const r = Math.PI / 180, y = Math.sin((b.lon - a.lon) * r) * Math.cos(b.lat * r), x = Math.cos(a.lat * r) * Math.sin(b.lat * r) - Math.sin(a.lat * r) * Math.cos(b.lat * r) * Math.cos((b.lon - a.lon) * r); return (Math.atan2(y, x) / r + 360) % 360; };
const fmtKm = (t, m) => `${(m / 1000).toFixed(1).replace('.', t.locale?.startsWith('en') ? '.' : ',')} km`;

function Elev({ plan, prog, total }) {
  const e = plan.elev || [];
  if (e.length < 2) return null;
  const W = 300, H = 46, mx = Math.max(...e.map((x) => x.h)), mn = Math.min(...e.map((x) => x.h)), hh = Math.max(10, mx - mn), K = e[e.length - 1].km || 1;
  const px = (km) => (km / K) * W, py = (h) => H - 4 - ((h - mn) / hh) * (H - 10);
  const d = `M0,${H} ${e.map((x) => `L${px(x.km).toFixed(1)},${py(x.h).toFixed(1)}`).join(' ')} L${W},${H} Z`;
  return (
    <svg className="nv-elev" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="navi-elev">
      <path d={d} fill="rgba(124,58,237,.25)" stroke="#7c3aed" strokeWidth="1.5" />
      {(plan.stops || []).map((s) => <text key={s.id} x={px(s.at)} y="11" fontSize="10" textAnchor="middle">{STOP_ICON[s.kind]}</text>)}
      <line x1={px((prog / total) * K)} x2={px((prog / total) * K)} y1="0" y2={H} stroke="#f97316" strokeWidth="2.5" />
    </svg>
  );
}

// Komoot-like navigation banner while recording along a guide route
export default function NaviBar({ t, plans }) {
  const rec = useTrackRec();
  const plan = useMemo(() => (rec?.status === 'rec' ? (plans || []).find((p) => p.id === rec.ref) : null), [rec?.ref, rec?.status, plans]); // eslint-disable-line react-hooks/exhaustive-deps
  const [nav, setNav] = useState(null);
  const [st, setSt] = useState(null);
  const mem = useRef({ said: {}, idx: 0, offN: 0, busy: false, last: null });
  useEffect(() => {
    mem.current = { said: {}, idx: 0, offN: 0, busy: false, last: null };
    if (!plan) { setNav(null); return; }
    if (plan.steps?.length) { const n = buildNav(plan.pts, plan.steps); mem.current.total0 = n.total; setNav(n); return; }
    const a = plan.pts[0], b = plan.pts[plan.pts.length - 1];
    const keep = (n) => { mem.current.total0 = n.total; setNav(n); };
    fetchRoute(a, b, plan.mode).then((r) => keep(buildNav(r.pts, r.steps))).catch(() => keep(buildNav(plan.pts, [{ lat: b[0], lon: b[1], type: 'arrive', mod: '' }])));
  }, [plan?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const c = rec?.cur, m = mem.current;
    if (!nav || !c) return;
    const s = navState(nav, c, m.idx);
    m.idx = s.idx;
    setSt(s);
    if (m.last && geoMoved(m.last, c)) window.dispatchEvent(new CustomEvent('giss-navi-view', { detail: { lat: c.lat, lon: c.lon, bearing: bearing(m.last, c) } }));
    if (!m.last || geoMoved(m.last, c)) m.last = c;
    if (s.next) {
      const k = `${s.next.at}`;
      for (const lim of [200, 30]) if (s.dist <= lim && !m.said[k + lim] && !(m.quiet > Date.now())) { m.said[k + lim] = 1; speak(stepText(t, s.next, s.dist), true); }
    }
    m.offN = s.off > (plan.mode === 'foot' ? 60 : 50) ? m.offN + 1 : 0;
    // walkers often leave the line briefly: recalc at most every 60 s, announce at most every 3 min
    if (m.offN >= 3 && !m.busy && navigator.onLine && !(m.nextRe > Date.now())) {
      m.busy = true; m.nextRe = Date.now() + 60000;
      const b = nav.pts[nav.pts.length - 1];
      if (!(m.nextSay > Date.now())) { m.nextSay = Date.now() + 180000; speak(t.nvReroute, true); }
      fetchRoute([c.lat, c.lon], b, plan.mode).then((r) => { setNav(buildNav(r.pts, r.steps)); m.idx = 0; m.said = {}; m.quiet = Date.now() + 3500; window.dispatchEvent(new CustomEvent('giss-agent-route', { detail: { pts: r.pts, name: plan.name, stops: plan.stops, keep: true, noFit: true } })); })
        .catch(() => {}).finally(() => { m.busy = false; m.offN = 0; });
    }
  }, [rec?.cur?.t, nav]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (plan) window.dispatchEvent(new CustomEvent('giss-agent-route', { detail: { pts: plan.pts, name: plan.name, stops: plan.stops, keep: true } })); }, [plan?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!plan || !nav) return null;
  const k = st?.next ? stepKey(st.next) : 'straight', Ic = ICON[k] || ArrowUp;
  const kmh = plan.mode === 'foot' ? 4.5 : 16, eta = st ? new Date(Date.now() + (st.left / 1000 / kmh) * 3.6e6).toLocaleTimeString(t.locale, { hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <>
      <div className={`navi-bar ${st?.off > 50 ? 'off' : ''}`} data-testid="navi-bar">
        <span className="nv-ico" data-testid="navi-turn-icon"><Ic size={38} strokeWidth={2.6} /></span>
        <div className="nv-txt">
          <b data-testid="navi-turn-text">{st ? (st.next ? stepText(t, st.next, st.dist) : t.nv_arrive) : t.nvWait}</b>
          <small data-testid="navi-remaining">{st ? `${fmtKm(t, st.left)} · ${t.nvEta} ${eta}${st.off > 50 ? ` · ${t.nvOff.replace('{m}', Math.round(st.off))}` : ''}` : <Navigation size={12} />}</small>
        </div>
      </div>
      {(plan.elev || []).length > 1 && <div className="navi-elev-wrap"><Elev plan={plan} prog={st ? Math.max(0, (mem.current.total0 || nav.total) - st.left) : 0} total={mem.current.total0 || nav.total} /></div>}
    </>
  );
}
const geoMoved = (a, b) => Math.abs(a.lat - b.lat) + Math.abs(a.lon - b.lon) > 0.00004;
